import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Package, Search, Loader2, ArrowLeft, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pendiente_pago: 'bg-amber-100 text-amber-800',
  en_proceso: 'bg-blue-100 text-blue-800',
  en_produccion: 'bg-indigo-100 text-indigo-800',
  listo: 'bg-green-100 text-green-800',
  entregado: 'bg-green-200 text-green-900',
  cancelado: 'bg-red-100 text-red-800',
};

const PAYMENT_COLORS = {
  pending: 'bg-amber-100 text-amber-800',
  paid: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
};

const PRODUCTION_STAGES = {
  pendiente: { label: 'Pendiente', step: 0 },
  diseno: { label: 'Diseño', step: 1 },
  impresion: { label: 'Impresión', step: 2 },
  suaje: { label: 'Suaje', step: 3 },
  pegado: { label: 'Pegado', step: 4 },
  empaque: { label: 'Empaque', step: 5 },
  listo: { label: 'Listo', step: 6 },
};

const OrderTracking = () => {
  const { orderNumber: urlOrderNumber } = useParams();
  const navigate = useNavigate();
  const [folioNumber, setFolioNumber] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const searchOrder = async (number) => {
    const num = (number || folioNumber).trim();
    if (!num) return;
    const query = `KAHA-${num}`;
    setLoading(true);
    setError('');
    setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/track/${query}`);
      setOrder(res.data);
    } catch {
      setError('No encontramos un pedido con ese folio. Verifica e intenta de nuevo.');
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (urlOrderNumber) {
      // If URL has full KAHA-XXXXX, extract just the number part
      const num = urlOrderNumber.replace(/^KAHA-/i, '');
      setFolioNumber(num);
      searchOrder(num);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const currentStage = order ? PRODUCTION_STAGES[order.production_stage] || PRODUCTION_STAGES.pendiente : null;

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <Package className="w-8 h-8 text-accent" />
            <span className="text-xl font-bold text-primary">KAHA</span>
          </a>
          <span className="text-sm text-muted-foreground">Rastreo de pedido</span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-6 gap-2" data-testid="btn-back-home">
          <ArrowLeft className="w-4 h-4" /> Volver al inicio
        </Button>

        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-primary mb-2">Rastrea tu pedido</h1>
          <p className="text-muted-foreground">Ingresa tu número de folio para ver el estado de tu pedido</p>
        </div>

        <div className="flex gap-3 mb-8">
          <div className="flex flex-1 items-center border rounded-md bg-white focus-within:ring-2 focus-within:ring-accent/40 focus-within:border-accent overflow-hidden">
            <span className="pl-4 pr-1 text-lg font-mono font-semibold text-muted-foreground select-none" data-testid="tracking-prefix">KAHA-</span>
            <input
              type="text"
              inputMode="numeric"
              value={folioNumber}
              onChange={(e) => setFolioNumber(e.target.value.replace(/\D/g, ''))}
              placeholder="00000"
              className="flex-1 h-12 text-lg font-mono bg-transparent outline-none pr-3"
              onKeyDown={(e) => e.key === 'Enter' && searchOrder()}
              maxLength={6}
              data-testid="input-tracking-number"
            />
          </div>
          <Button
            onClick={() => searchOrder()}
            disabled={loading || !folioNumber.trim()}
            className="h-12 px-6 bg-accent hover:bg-accent/90"
            data-testid="btn-search-order"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />}
          </Button>
        </div>

        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 text-center" data-testid="tracking-error">
            {error}
          </div>
        )}

        {order && (
          <Card className="shadow-soft" data-testid="tracking-result">
            <CardHeader className="border-b bg-[#58655A] text-white">
              <CardTitle className="flex items-center justify-between">
                <span>{order.order_number}</span>
                <span className={`text-xs px-3 py-1 rounded-full ${STATUS_COLORS[order.customer_status] || 'bg-gray-100 text-gray-800'}`}>
                  {order.customer_status_label}
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Status badges */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Pago</p>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium ${PAYMENT_COLORS[order.payment_status] || 'bg-gray-100 text-gray-800'}`} data-testid="tracking-payment-status">
                    {order.payment_status_label}
                  </span>
                </div>
                <div className="p-3 bg-muted/50 rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Arte</p>
                  <span className="text-xs px-2 py-1 rounded-full font-medium bg-blue-100 text-blue-800" data-testid="tracking-artwork-status">
                    {order.artwork_status_label}
                  </span>
                </div>
              </div>

              {/* Production pipeline */}
              {order.payment_status === 'paid' && (
                <div>
                  <p className="text-sm font-semibold mb-3">Progreso de producción</p>
                  <div className="flex items-center gap-1">
                    {Object.entries(PRODUCTION_STAGES).filter(([k]) => k !== 'pendiente').map(([key, stage]) => (
                      <div key={key} className="flex-1">
                        <div
                          className={`h-2 rounded-full transition-all ${
                            currentStage && stage.step <= currentStage.step ? 'bg-accent' : 'bg-muted'
                          }`}
                        />
                        <p className={`text-[10px] mt-1 text-center ${
                          currentStage && stage.step <= currentStage.step ? 'text-accent font-medium' : 'text-muted-foreground'
                        }`}>
                          {stage.label}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Dimensiones</span>
                  <span>{parseFloat(order.dimensions?.length / 10)} x {parseFloat(order.dimensions?.width / 10)} x {parseFloat(order.dimensions?.height / 10)} cm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Material</span>
                  <span>{order.material?.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Cantidad</span>
                  <span>{order.quantity?.toLocaleString()} cajas</span>
                </div>
                {order.shipping_method && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Envío</span>
                    <span>{order.shipping_method === 'pickup' ? 'Recoger en planta' : order.shipping_carrier}</span>
                  </div>
                )}
                <div className="flex justify-between font-bold border-t pt-2">
                  <span>Total</span>
                  <span className="text-accent">${order.total_amount?.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN</span>
                </div>
              </div>

              {/* Invoice CTA */}
              {order.payment_status === 'paid' && !order.invoice_requested && (
                <div className="p-4 bg-muted/50 rounded-lg text-center">
                  <p className="text-sm text-muted-foreground mb-2">¿Necesitas factura para este pedido?</p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate(`/facturacion/${order.id}`)}
                    className="gap-2"
                    data-testid="btn-request-invoice"
                  >
                    <FileText className="w-4 h-4" />
                    Solicitar factura
                  </Button>
                </div>
              )}

              {order.invoice_requested && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center">
                  Factura solicitada — Status: {order.invoice_status === 'pending' ? 'Pendiente' : order.invoice_status === 'generated' ? 'Generada' : order.invoice_status === 'sent' ? 'Enviada' : order.invoice_status}
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                Creado: {new Date(order.created_at).toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default OrderTracking;
